import styled, { css } from 'styled-components'
import { ButtonGroup } from '@components'
import { FaScaleUnbalanced } from 'react-icons/fa6'
import {
  ENTRY_ATTACHMENT_BUTTON_SIZE_PX,
  ENTRY_ROW_MIN_HEIGHT_PX,
  ENTRY_ROW_PAD_Y_PX,
  REPORT_DRILL_COLUMN_BG,
  REPORT_LINE_ITEM_MIN_WIDTH_PX,
  REPORT_ROW_PAD_Y_PX,
  REPORT_VALUE_CELL_PAD_X_PX,
  narrowRowLabelIndentPx,
  narrowRowPadLeftPx,
  REPORT_ROW_BASE_PAD_LEFT_PX,
  rowLabelIndentPx
} from './periodUtils'

/** Bleed drill-column highlight through row vertical padding so it fills the full row height. */
const drillColumnHighlightFill = css`
  margin-block: -${REPORT_ROW_PAD_Y_PX}px;
  padding-block: ${REPORT_ROW_PAD_Y_PX}px;
`

const valueCellPadding = css`
  padding-inline: ${REPORT_VALUE_CELL_PAD_X_PX}px;
`

export const PageInner = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm}rem;
  width: 100%;
  min-width: 0;
  flex: 1;
  min-height: 0;
  height: 100%;
  overflow: hidden;
`

export const ReportViewport = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm}rem;
  min-height: 0;
  min-width: 0;
`

export const Toolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  flex-shrink: 0;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  background: white;
  border-radius: ${({ theme }) => theme.borderRadius.md}rem;
  border: 1px solid ${({ theme }) => theme.colors.neutralGray};
`

export const ToolbarGroup = styled.div<{ $nowrap?: boolean; $end?: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: none;
  flex-wrap: ${({ $nowrap }) => ($nowrap ? 'nowrap' : 'wrap')};

  ${({ $end }) =>
    $end &&
    css`
      margin-left: auto;
    `}
`

export const ToolbarChip = styled.button<{ $active?: boolean; $muted?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  height: 34px;
  padding: 0 11px;
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.md}rem;
  background: ${({ theme, $active }) => ($active ? '#e8f4fe' : theme.colors.neutralGray)};
  color: ${({ theme, $active, $muted }) =>
    $active ? theme.colors.primary : $muted ? theme.colors.metalGray : theme.colors.neutralBlack};
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  font-weight: ${({ $active }) => ($active ? 700 : 500)};
  cursor: ${({ $muted }) => ($muted ? 'default' : 'pointer')};
  white-space: nowrap;

  &:hover {
    filter: ${({ $muted }) => ($muted ? 'none' : 'brightness(0.97)')};
  }

  &:disabled {
    opacity: 0.55;
    cursor: default;
  }
`

export const ToolbarDivider = styled.div`
  width: 1px;
  height: 24px;
  background: ${({ theme }) => theme.colors.neutralGray};
  flex: none;
  margin: 0 4px;
`

export const ToolbarButtonGroup = styled(ButtonGroup)`
  & > button {
    justify-content: center;
  }
`

export const ColumnHeaderLabel = styled.span`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  padding: 2px 0;
  color: ${({ theme }) => theme.colors.metalGray};
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.35px;
  text-transform: uppercase;
  font-variant-numeric: tabular-nums;
  text-align: right;
`

export const ReportCard = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  background: white;
  border-radius: ${({ theme }) => theme.borderRadius.md}rem;
  border: 1px solid ${({ theme }) => theme.colors.neutralGray};
  min-width: 0;
  /* Column header menus drop out of the card, so it must not clip them. */
  overflow: visible;
`

/** Ancestor class while placeholder figures from the previous range are on screen. */
export const STALE_FIGURES_CLASS = 'is-stale-figures'

export const ReportBodyScroll = styled.div`
  flex: 1;
  min-height: 0;
  min-width: 0;
  overflow: auto;
  -webkit-overflow-scrolling: touch;
`

export const ReportScrollArea = styled.div`
  width: 100%;
  max-width: 100%;
  min-width: 0;
`

export const ReportTableSurface = styled.div<{ $minWidth: number }>`
  min-width: ${({ $minWidth }) => $minWidth}px;
  width: 100%;
`

export const ReportHeader = styled.div`
  display: flex;
  flex-shrink: 0;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 16px 10px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.neutralGray};
`

export const ReportHeaderTitle = styled.div`
  font-size: ${({ theme }) => theme.fontSize.xs}rem;
  font-weight: 600;
  letter-spacing: 0.45px;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.metalGray};
`

export const ReportHeaderTitleGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

export const ReportHeaderMeta = styled.div`
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  color: ${({ theme }) => theme.colors.metalGray};
`

/** Summary strip above the EU sales table (company / VAT id / period). */
export const EuSummaryCard = styled.div`
  display: flex;
  flex-shrink: 0;
  flex-wrap: wrap;
  gap: 48px;
  padding: 18px 20px;
  background: white;
  border-radius: ${({ theme }) => theme.borderRadius.md}rem;
  border: 1px solid ${({ theme }) => theme.colors.neutralGray};
`

export const EuSummaryBlock = styled.div`
  min-width: 0;
`

export const EuSummaryLabel = styled.div`
  margin-bottom: 8px;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  color: #a0aab5;
`

export const EuSummaryValue = styled.div<{ $muted?: boolean; $warn?: boolean }>`
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  color: ${({ theme, $muted, $warn }) =>
    $warn ? theme.colors.warning : $muted ? '#7b8794' : theme.colors.neutralBlack};
  font-weight: ${({ $muted, $warn }) => ($warn ? 600 : $muted ? 400 : 500)};
  font-variant-numeric: tabular-nums;
`

/** Country column — BE sends formatted values like "FI - Finland". */
export const EuCountryCode = styled.span`
  width: 140px;
  flex: none;
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  color: ${({ theme }) => theme.colors.metalGray};
  font-variant-numeric: tabular-nums;
  text-align: right;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

export const JournalUnbalancedIcon = styled(FaScaleUnbalanced)`
  color: ${({ theme }) => theme.colors.error};
  font-size: ${({ theme }) => theme.fontSize.md}rem;
  vertical-align: middle;
`

/**
 * Fixed-width slot matching the attachment button hit target.
 * Empty slots keep the gutter so columns stay aligned without the paperclip.
 */
export const AttachmentSlot = styled.div`
  min-width: ${ENTRY_ATTACHMENT_BUTTON_SIZE_PX}px;
  width: ${ENTRY_ATTACHMENT_BUTTON_SIZE_PX}px;
  flex: none;
  align-self: center;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  position: relative;

  &:empty {
    width: ${ENTRY_ATTACHMENT_BUTTON_SIZE_PX}px;
  }
`

export const EntryDocumentLink = styled.button`
  appearance: none;
  background: none;
  border: none;
  padding: 0;
  margin: 0;
  cursor: pointer;
  font: inherit;
  color: ${({ theme }) => theme.colors.primary};
  text-decoration: none;
  font-variant-numeric: tabular-nums;

  &:hover {
    text-decoration: underline;
  }
`

/** Fits `XXXXXX-XX` voucher numbers; keeps descriptions aligned across rows. */
export const ENTRY_VOUCHER_NUMBER_MIN_WIDTH = '9ch'

export const EntryVoucherRow = styled.span`
  display: flex;
  align-items: baseline;
  flex: 1;
  align-self: center;
  min-width: ${REPORT_LINE_ITEM_MIN_WIDTH_PX}px;
  overflow: hidden;
  font-size: ${({ theme }) => theme.fontSize.xs}rem;
  font-weight: 400;
  color: ${({ theme }) => theme.colors.neutralBlack};
`

export const EntryVoucherNumber = styled.span`
  flex: none;
  min-width: ${ENTRY_VOUCHER_NUMBER_MIN_WIDTH};
  padding-right: 8px;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
`

export const EntryVoucherDescription = styled.span<{ $semibold?: boolean }>`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: ${({ theme }) => theme.fontSize.xs}rem;
  color: ${({ theme }) => theme.colors.metalGray};
  font-weight: ${({ $semibold }) => ($semibold ? 600 : 400)};
`

/** Name sits with Country/VAT on the left; does not expand across the row. */
export const EuNameCell = styled.span<{
  $strong?: boolean
  $semibold?: boolean
  $warn?: boolean
}>`
  flex: 0 1 220px;
  width: 220px;
  max-width: 220px;
  min-width: 0;
  text-align: right;
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  font-weight: ${({ $strong, $semibold }) => ($strong ? 600 : $semibold ? 500 : 400)};
  color: ${({ theme, $warn }) => ($warn ? theme.colors.warning : theme.colors.neutralBlack)};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

/** Pushes Type of supply / Amount to the right edge. */
export const EuRowSpacer = styled.div`
  flex: 1;
  min-width: 0;
`

export const ColumnHeaderRow = styled.div`
  position: sticky;
  top: 0;
  z-index: 2;
  display: flex;
  flex-wrap: nowrap;
  align-items: stretch;
  gap: 8px;
  padding: ${REPORT_ROW_PAD_Y_PX}px 16px;
  padding-left: ${REPORT_ROW_BASE_PAD_LEFT_PX}px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.neutralGray};
  background: #f7fafd;
  box-shadow: 0 1px 0 ${({ theme }) => theme.colors.neutralGray};

  @media (max-width: ${({ theme }) => theme.breakpoint.md}) {
    padding-left: ${narrowRowPadLeftPx(REPORT_ROW_BASE_PAD_LEFT_PX)}px;
  }
`

export const LineItemHeader = styled.div<{ $align?: 'left' | 'right' }>`
  flex: 1;
  min-width: ${REPORT_LINE_ITEM_MIN_WIDTH_PX}px;
  align-self: center;
  text-align: ${({ $align }) => $align || 'left'};
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.metalGray};
`

export const ValueHeader = styled.div<{
  $width?: string
  $align?: 'left' | 'right'
  $highlighted?: boolean
}>`
  width: ${({ $width }) => $width || '128px'};
  flex: none;
  min-width: ${({ $width }) => $width || '128px'};
  max-width: ${({ $width }) => $width || '128px'};
  align-self: stretch;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  background: ${({ $highlighted }) => ($highlighted ? REPORT_DRILL_COLUMN_BG : 'transparent')};
  ${({ $highlighted }) => ($highlighted ? drillColumnHighlightFill : '')}
  ${valueCellPadding}
  text-align: ${({ $align }) => $align || 'right'};
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.35px;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.metalGray};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

export const ColumnHeaderCell = styled.div<{ $width?: string; $highlighted?: boolean }>`
  width: ${({ $width }) => $width || '128px'};
  flex: none;
  min-width: ${({ $width }) => $width || '128px'};
  max-width: ${({ $width }) => $width || '128px'};
  align-self: stretch;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  background: ${({ $highlighted }) => ($highlighted ? REPORT_DRILL_COLUMN_BG : 'transparent')};
  ${({ $highlighted }) => ($highlighted ? drillColumnHighlightFill : '')}
  ${valueCellPadding}
`

export const ColumnHeaderValueSlot = styled.div`
  width: 100%;
  min-width: 0;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: flex-end;

  button {
    max-width: 100%;
    min-width: 0;
    padding-left: 6px;
    padding-right: 6px;
    font-variant-numeric: tabular-nums;

    > span {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      font-size: 10px;
      font-weight: 600;
      letter-spacing: 0.35px;
      text-transform: uppercase;
    }
  }
`

export const ColumnHeaderButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  max-width: 100%;
  min-width: 0;
  padding: 2px 4px;
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.sm}rem;
  background: transparent;
  color: ${({ theme }) => theme.colors.metalGray};
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.35px;
  text-transform: uppercase;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  overflow: hidden;
  cursor: pointer;

  span {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &:hover {
    background: #e6eef6;
    color: ${({ theme }) => theme.colors.neutralBlack};
  }
`

/** Sits after the last period column; spacer on value rows keeps columns aligned. */
export const ADD_COLUMN_SLOT_WIDTH = '26px'

export const AddColumnButton = styled.button`
  flex: none;
  width: ${ADD_COLUMN_SLOT_WIDTH};
  height: 26px;
  align-self: center;
  /* Row gap is 8px; this extra 8px matches the mockup's 16px header gap. */
  margin-left: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px dashed #b9c4d0;
  border-radius: 0.6rem;
  background: transparent;
  color: #1b98f5;
  font-size: 10px;
  cursor: pointer;

  &:hover {
    border-color: #1b98f5;
    background: #f2f6fa;
    color: #1b98f5;
  }

  &:disabled {
    opacity: 0.4;
    cursor: default;
    background: transparent;
  }
`

/** Same footprint as AddColumnButton so value cells stay under their headers. */
export const AddColumnSpacer = styled.div`
  flex: none;
  width: ${ADD_COLUMN_SLOT_WIDTH};
  height: 1px;
  margin-left: 8px;
  align-self: center;
`

export const MenuItemHint = styled.div`
  padding-top: 3px;
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  font-weight: 400;
  text-transform: none;
  letter-spacing: normal;
  white-space: normal;
  max-width: 240px;
`

export const MenuSectionLabel = styled.div`
  padding: 10px 12px 4px;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.metalGray};
`

const responsiveRowBasePadLeft = css`
  padding-left: ${REPORT_ROW_BASE_PAD_LEFT_PX}px;

  @media (max-width: ${({ theme }) => theme.breakpoint.md}) {
    padding-left: ${narrowRowPadLeftPx(REPORT_ROW_BASE_PAD_LEFT_PX)}px;
  }
`

const responsiveRowPadLeft = css<{ $padLeft?: number }>`
  padding-left: ${({ $padLeft }) =>
    $padLeft != null ? `${$padLeft}px` : `${REPORT_ROW_BASE_PAD_LEFT_PX}px`};

  @media (max-width: ${({ theme }) => theme.breakpoint.md}) {
    padding-left: ${narrowRowPadLeftPx(REPORT_ROW_BASE_PAD_LEFT_PX)}px;
  }
`

const responsiveEntryRowPadLeft = css`
  padding-left: ${REPORT_ROW_BASE_PAD_LEFT_PX}px;

  @media (max-width: ${({ theme }) => theme.breakpoint.md}) {
    padding-left: ${narrowRowPadLeftPx(REPORT_ROW_BASE_PAD_LEFT_PX)}px;
  }
`

const responsiveLabelIndent = css<{ $indentFrom?: number }>`
  padding-left: ${({ $indentFrom }) => `${rowLabelIndentPx($indentFrom)}px`};

  @media (max-width: ${({ theme }) => theme.breakpoint.md}) {
    padding-left: ${({ $indentFrom }) => `${narrowRowLabelIndentPx($indentFrom)}px`};
  }
`

export const ReportRow = styled.div<{
  $padLeft?: number
  $bg?: string
  $warn?: boolean
  $strong?: boolean
  $clickable?: boolean
  $borderStrong?: boolean
  /** When true, keeps legacy full-row indent (entry drill-down panels). */
  $indentRow?: boolean
  /** Entry drill-down table rows inside an expanded account panel. */
  $entryRow?: boolean
}>`
  display: flex;
  flex-wrap: nowrap;
  align-items: stretch;
  gap: 8px;
  padding: ${REPORT_ROW_PAD_Y_PX}px 16px;
  ${({ $indentRow, $entryRow }) =>
    $entryRow
      ? css`
          align-items: center;
          min-height: ${ENTRY_ROW_MIN_HEIGHT_PX}px;
          padding: ${ENTRY_ROW_PAD_Y_PX}px 16px;
          ${responsiveEntryRowPadLeft}
        `
      : $indentRow
      ? responsiveRowPadLeft
      : responsiveRowBasePadLeft}
  background: ${({ $bg, $warn, theme }) =>
    $bg ? $bg : $warn ? `color-mix(in srgb, ${theme.colors.warning} 16%, white)` : 'transparent'};
  border-bottom: ${({ $borderStrong, theme }) =>
    $borderStrong
      ? `1.5px solid ${theme.colors.neutralGray}`
      : `1px solid ${theme.colors.neutralGray}`};
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};
  font-weight: ${({ $strong }) => ($strong ? 600 : 400)};

  &:hover {
    background: ${({ $clickable, $warn, $bg, theme }) =>
      $clickable
        ? '#f7fafd'
        : $bg
        ? $bg
        : $warn
        ? `color-mix(in srgb, ${theme.colors.warning} 16%, white)`
        : undefined};
  }
`

export const Caret = styled.span`
  width: 14px;
  flex: none;
  align-self: center;
  color: #8fa3b5;
  font-size: 11px;
  display: inline-flex;
  justify-content: center;
`

export const AccountCode = styled.span<{ $header?: boolean }>`
  width: 46px;
  flex: none;
  align-self: center;
  font-variant-numeric: tabular-nums;
  ${({ $header, theme }) =>
    $header
      ? `
    font-size: ${theme.fontSize.px10}rem;
    font-weight: 600;
    letter-spacing: 0.4px;
    text-transform: uppercase;
    color: ${theme.colors.metalGray};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  `
      : `
    font-size: ${theme.fontSize.xs}rem;
    color: ${theme.colors.metalGray};
  `}
`

/** Account number shown inline before the account name in the line-item column. */
export const InlineAccountCode = styled.span`
  margin-right: 8px;
  font-size: ${({ theme }) => theme.fontSize.xs}rem;
  color: ${({ theme }) => theme.colors.metalGray};
  font-variant-numeric: tabular-nums;
`

/** Fixed-width date column for entry breakdowns (DD.MM.YYYY). */
export const DateCell = styled.span<{ $header?: boolean }>`
  width: 92px;
  flex: none;
  align-self: center;
  font-size: ${({ theme, $header }) => ($header ? '10px' : `${theme.fontSize.xs}rem`)};
  font-weight: ${({ $header }) => ($header ? 600 : 400)};
  letter-spacing: ${({ $header }) => ($header ? '0.4px' : 'normal')};
  text-transform: ${({ $header }) => ($header ? 'uppercase' : 'none')};
  color: ${({ theme }) => theme.colors.metalGray};
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

export const RowLabel = styled.span<{
  $strong?: boolean
  $semibold?: boolean
  $link?: boolean
  $header?: boolean
  $align?: 'left' | 'right'
  /** Total row indent target; only the label shifts so value columns stay aligned. */
  $indentFrom?: number
}>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex: 1;
  min-width: ${REPORT_LINE_ITEM_MIN_WIDTH_PX}px;
  align-self: center;
  text-align: ${({ $align }) => $align || 'left'};
  font-size: ${({ theme, $header }) => ($header ? '10px' : `${theme.fontSize.sm}rem`)};
  font-weight: ${({ $strong, $semibold, $header }) =>
    $header || $strong ? 600 : $semibold ? 500 : 400};
  letter-spacing: ${({ $header }) => ($header ? '0.4px' : 'normal')};
  text-transform: ${({ $header }) => ($header ? 'uppercase' : 'none')};
  color: ${({ theme, $link, $header }) =>
    $header ? theme.colors.metalGray : $link ? theme.colors.primary : theme.colors.neutralBlack};
  overflow: hidden;
  ${responsiveLabelIndent}
`

export const RowLabelText = styled.span`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

/** Muted in-table copy for ledger/journal empty periods (matches EU VAT empty row). */
export const ReportEmptyMessage = styled(RowLabelText)`
  color: #7b8794;
`

export const RowMeta = styled.span<{ $warn?: boolean }>`
  margin-left: 8px;
  font-size: ${({ theme }) => theme.fontSize.xs}rem;
  color: ${({ theme, $warn }) => ($warn ? theme.colors.warning : theme.colors.metalGray)};
  font-weight: ${({ $warn }) => ($warn ? 500 : 400)};
  text-transform: none;
  letter-spacing: normal;
`

export const ValueCell = styled.div<{
  $width?: string
  $muted?: boolean
  $strong?: boolean
  $semibold?: boolean
  $header?: boolean
  $align?: 'left' | 'right'
  $warn?: boolean
  $highlighted?: boolean
}>`
  width: ${({ $width }) => $width || '128px'};
  flex: none;
  min-width: ${({ $width }) => $width || '128px'};
  max-width: ${({ $width }) => $width || '128px'};
  align-self: stretch;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  background: ${({ $highlighted }) => ($highlighted ? REPORT_DRILL_COLUMN_BG : 'transparent')};
  ${({ $highlighted }) => ($highlighted ? drillColumnHighlightFill : '')}
  ${valueCellPadding}
  text-align: ${({ $align }) => $align || 'right'};
  font-size: ${({ theme, $header }) => ($header ? '10px' : `${theme.fontSize.sm}rem`)};
  font-weight: ${({ $strong, $semibold, $header }) =>
    $header || $strong ? 600 : $semibold ? 500 : 400};
  letter-spacing: ${({ $header }) => ($header ? '0.35px' : 'normal')};
  text-transform: ${({ $header }) => ($header ? 'uppercase' : 'none')};
  color: ${({ theme, $muted, $header, $warn }) =>
    $warn
      ? theme.colors.warning
      : $header || $muted
      ? theme.colors.metalGray
      : theme.colors.neutralBlack};
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  transition: opacity 0.15s;

  .${STALE_FIGURES_CLASS} & {
    opacity: 0.3;
  }
`

export const WarnMark = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.warning};

  svg {
    flex: none;
    font-size: 11px;
  }
`

export const ReportNoticeBanner = styled.div<{ $tone: 'info' | 'warning' }>`
  padding: 11px 20px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.neutralGray};
  background: ${({ theme, $tone }) =>
    $tone === 'warning'
      ? `color-mix(in srgb, ${theme.colors.warning} 16%, white)`
      : `color-mix(in srgb, ${theme.colors.info} 12%, white)`};
`

export const FootNote = styled.div`
  flex-shrink: 0;
  border-top: 1px solid ${({ theme }) => theme.colors.neutralGray};
  padding: 12px 16px 16px;
  font-size: ${({ theme }) => theme.fontSize.xs}rem;
  color: ${({ theme }) => theme.colors.metalGray};
`

export const EntryPanel = styled.div`
  background: ${REPORT_DRILL_COLUMN_BG};
  border-bottom: 1px solid ${({ theme }) => theme.colors.neutralGray};

  ${ValueCell} {
    align-self: center;
  }
`

export const EntryHeaderRow = styled.div`
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  gap: 8px;
  min-height: ${ENTRY_ROW_MIN_HEIGHT_PX}px;
  padding: ${ENTRY_ROW_PAD_Y_PX}px 16px;
  ${responsiveEntryRowPadLeft}
  background: #eff4f9;
  border-bottom: 1px solid ${({ theme }) => theme.colors.neutralGray};
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.metalGray};
`

export const EntryPanelPaginationBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 0.5rem;
  padding: 0.5rem 16px;
  ${responsiveEntryRowPadLeft}
  border-top: 1px solid ${({ theme }) => theme.colors.neutralGray};
  background: ${REPORT_DRILL_COLUMN_BG};
`

export const EntryPanelPaginationSummary = styled.span`
  font-size: ${({ theme }) => theme.fontSize.xs}rem;
  color: ${({ theme }) => theme.colors.iconGray};
`

export const EntryPanelPaginationNav = styled.nav`
  display: flex;
  align-items: center;
  gap: 0.2rem;
`

export const EntryPanelPaginationButton = styled.button<{ $active?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 26px;
  height: 26px;
  margin: 0;
  padding: 0;
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.sm}rem;
  background: ${({ theme, $active }) => ($active ? theme.colors.info : 'transparent')};
  color: ${({ theme, $active }) => ($active ? theme.colors.neutralWhite : theme.colors.metalGray)};
  font-size: ${({ theme }) => theme.fontSize.xs}rem;
  font-weight: 600;
  line-height: 1;
  cursor: ${({ $active }) => ($active ? 'default' : 'pointer')};
  transition: background 140ms ease-out, color 140ms ease-out;

  svg {
    width: 0.65rem;
    height: 0.65rem;
  }

  &:hover:not(:disabled) {
    background: ${({ theme, $active }) =>
      $active ? theme.colors.info : theme.colors.defaultHover};
  }

  &:disabled {
    opacity: 0.4;
    cursor: default;
  }
`

export const EntryPanelPaginationEllipsis = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  color: ${({ theme }) => theme.colors.iconGray};
  font-size: ${({ theme }) => theme.fontSize.xs}rem;
  font-weight: 600;
  line-height: 1;
  user-select: none;
`

export const DateChip = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  height: 34px;
  padding: 0 11px;
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.md}rem;
  background: ${({ theme }) => theme.colors.neutralGray};
  color: ${({ theme }) => theme.colors.neutralBlack};
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;

  &:hover {
    filter: brightness(0.97);
  }
`
